import { useEffect, useState, useRef, useCallback } from "react";

type RequestFunction<T> = () => Promise<T> | T;

/**
 * Custom hook for polling an endpoint at regular intervals
 * @param trigger - Boolean flag to start polling
 * @param cancelPolling - Boolean flag to stop polling
 * @param requestFunction - Async function to call for polling
 * @param options - Optional configuration for polling behavior
 */
interface PollingOptions {
  interval?: number; // Polling interval in ms
  maxRetries?: number; // Maximum number of retries
  maxPollingTime?: number; // Maximum total polling time in ms
  initialDelay?: number;
  pollingId?: string; // Unique identifier for this polling session
  debug?: boolean; // Enable debug logging
}

const DEFAULT_OPTIONS: Required<PollingOptions> = {
  interval: 2000, // 2 seconds
  maxRetries: 7,
  maxPollingTime: 15000, // 15 seconds
  initialDelay: 0,
  pollingId: "default", // Default polling ID
  debug: process.env.NODE_ENV === "development", // Enable debug logging in development
};

function usePolling<T>(
  trigger: boolean,
  cancelPolling: boolean,
  requestFunction: RequestFunction<T>,
  options: PollingOptions = {}
) {
  const [response, setResponse] = useState<T | null>(null);
  const [polling, setPolling] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Use refs to track polling state to prevent race conditions
  const isPollingRef = useRef<boolean>(false);
  const retryCounterRef = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const initialPollRef = useRef<boolean>(false);
  const isMountedRef = useRef<boolean>(true);
  const pollingStartTimeRef = useRef<number>(0);
  const isInitialized = useRef<boolean>(false);

  const {
    interval,
    maxRetries,
    maxPollingTime,
    initialDelay,
    pollingId,
    debug,
  } = {
    ...DEFAULT_OPTIONS,
    ...options,
  } as Required<PollingOptions>;

  // Helper for debug logging
  const log = useCallback(
    (...args: any[]) => {
      if (debug) {
        console.log(`[usePolling:${pollingId}]`, ...args);
      }
    },
    [debug, pollingId]
  );

  // Helper to generate storage key for this polling session
  const getStorageKey = useCallback(() => {
    return `polling_session_${pollingId}`;
  }, [pollingId]);

  // Register this polling session in storage
  const registerActivePolling = useCallback(() => {
    if (!pollingId) return;

    try {
      localStorage.setItem(getStorageKey(), Date.now().toString());
    } catch (e) {
      // Silently fail if localStorage is not available
      log("Failed to register polling in localStorage:", e);
    }
  }, [getStorageKey, pollingId, log]);

  // Unregister this polling session from storage
  const unregisterActivePolling = useCallback(() => {
    if (!pollingId) return;

    try {
      localStorage.removeItem(getStorageKey());
    } catch (e) {
      // Silently fail if localStorage is not available
      log("Failed to unregister polling from localStorage:", e);
    }
  }, [getStorageKey, pollingId, log]);

  // Check if another instance is already polling with this ID
  const isAlreadyPolling = useCallback(() => {
    if (!pollingId) return false;

    try {
      const storedPolling = localStorage.getItem(getStorageKey());
      if (!storedPolling) return false;

      // If the stored polling timestamp is too old (over 2x maxPollingTime),
      // we consider it abandoned and allow a new session
      const pollingTimestamp = parseInt(storedPolling, 10);
      const now = Date.now();
      if (now - pollingTimestamp > maxPollingTime * 2) {
        log("Found abandoned polling session, clearing it");
        unregisterActivePolling();
        return false;
      }

      log("Another instance is already polling with this ID, skipping");
      return true;
    } catch (e) {
      log("Error checking for active polling:", e);
      return false;
    }
  }, [getStorageKey, maxPollingTime, pollingId, unregisterActivePolling, log]);

  const cleanup = useCallback(() => {
    log("Cleaning up polling resources");
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    isPollingRef.current = false;
    setPolling(false);
    unregisterActivePolling();
    retryCounterRef.current = 0;
  }, [unregisterActivePolling, log]);

  const poll = useCallback(async () => {
    // Check if we should stop polling
    if (
      !isMountedRef.current ||
      retryCounterRef.current >= maxRetries ||
      cancelPolling ||
      !trigger ||
      isPollingRef.current
    ) {
      log("Skipping poll - conditions not met:", {
        isMounted: isMountedRef.current,
        retryCount: retryCounterRef.current,
        maxRetries,
        cancelPolling,
        trigger,
        isPolling: isPollingRef.current,
      });
      cleanup();
      return;
    }

    // Track time elapsed since polling started to prevent excessive polling
    const now = Date.now();
    const timeElapsed = now - pollingStartTimeRef.current;
    if (timeElapsed > maxPollingTime) {
      log(`Polling timed out after ${timeElapsed}ms`);
      cleanup();
      setError(new Error(`Polling timed out after ${maxPollingTime}ms`));
      return;
    }

    isPollingRef.current = true;
    log(`Polling attempt ${retryCounterRef.current + 1} of ${maxRetries}`);

    try {
      const result = await requestFunction();
      if (isMountedRef.current) {
        setResponse(result);
        log("Poll response:", result);
        // Check for specific conditions that indicate polling should stop
        if (result) {
          // For most generic cases, check if there's a 'status' field to determine completion
          const status =
            typeof result === "object" && result !== null
              ? (result as any).status || (result as any).verificationStatus
              : undefined;
          if (
            status === "SUCCESS" ||
            status === "APPROVED" ||
            status === "REJECTED" ||
            status === "FAILED" ||
            status === "VERIFIED" ||
            status === "NOT_VERIFIED"
          ) {
            log(`Poll complete with status: ${status}, cleaning up`);
            cleanup();
            return;
          }

          // If no stopping condition is met but we got a result, continue polling
          retryCounterRef.current++;
        }
      }
    } catch (err) {
      log("Polling error:", err);
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
        cleanup();
      }
      return;
    } finally {
      isPollingRef.current = false;
    }
  }, [
    trigger,
    cancelPolling,
    requestFunction,
    cleanup,
    maxRetries,
    maxPollingTime,
    log,
  ]);

  const startPolling = useCallback(() => {
    // Only start polling if not already polling and if no other instance is polling
    if (isPollingRef.current || isAlreadyPolling()) {
      log(
        "Skipping poll start - already polling or another instance is polling"
      );
      return;
    }

    log("Starting polling");
    registerActivePolling();
    pollingStartTimeRef.current = Date.now();

    if (!initialPollRef.current) {
      initialPollRef.current = true;
      poll();
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(poll, interval);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        log(`Polling timed out after ${maxPollingTime}ms`);
        cleanup();
        setError(new Error(`Polling timed out after ${maxPollingTime}ms`));
      }
    }, maxPollingTime);
  }, [
    poll,
    interval,
    maxPollingTime,
    cleanup,
    isAlreadyPolling,
    registerActivePolling,
    log,
  ]);

  useEffect(() => {
    // Skip reinitialization if we've already done this once
    if (isInitialized.current) {
      return;
    }

    // Clear potentially stale polling entries
    try {
      const storedPolling = localStorage.getItem(getStorageKey());
      if (storedPolling) {
        const pollingTimestamp = parseInt(storedPolling, 10);
        const now = Date.now();
        // If the stored polling is older than an hour, consider it stale
        if (now - pollingTimestamp > 3600000) {
          log("Found stale polling entry, clearing it");
          localStorage.removeItem(getStorageKey());
        }
      }
    } catch (e) {
      // Silently fail if localStorage is not available
      log("Error checking for stale polling entries:", e);
    }

    isMountedRef.current = true;
    initialPollRef.current = false;
    isInitialized.current = true;

    return () => {
      log("Component unmounting, cleaning up");
      isMountedRef.current = false;
      cleanup();
      isInitialized.current = false;
    };
  }, [cleanup, log, getStorageKey]);

  useEffect(() => {
    // Start or stop polling based on trigger and cancelPolling
    if (trigger && !cancelPolling && !polling) {
      log("Initializing polling");
      setPolling(true);
      retryCounterRef.current = 0;
      setError(null);

      if (initialDelay > 0) {
        log(`Delaying polling start by ${initialDelay}ms`);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(startPolling, initialDelay);
      } else {
        startPolling();
      }
    } else if ((!trigger || cancelPolling) && polling) {
      log("Stopping polling due to trigger or cancelPolling change");
      cleanup();
    }
  }, [
    trigger,
    cancelPolling,
    polling,
    startPolling,
    cleanup,
    initialDelay,
    log,
  ]);

  const reset = useCallback(() => {
    log("Resetting polling state");
    cleanup();
    initialPollRef.current = false;
    setResponse(null);
    setError(null);
    setPolling(false);
    pollingStartTimeRef.current = 0;
  }, [cleanup, log]);

  return { response, polling, error, reset };
}

export default usePolling;
